import purchaseTracker from '@/utils/purchaseTracker';

/**
 * Purchase Mixin for Vue components
 * Provides purchase tracking functionality
 */
export default {
    data() {
        return {
            purchaseTracker: null
        };
    },
    mounted() {
        this.initPurchaseTracker();
    },
    methods: {
        /**
         * Initialize purchase tracker
         */
        initPurchaseTracker() {
            this.purchaseTracker = window.purchaseTracker || new purchaseTracker();
        },
        /**
         * Track purchase event
         * @param {Object} purchaseData - Purchase data
         */
        trackPurchase(purchaseData) {
            if (!this.purchaseTracker) {
                this.initPurchaseTracker();
            }
            if (!this.purchaseTracker) {
                console.error('❌ [PURCHASE] tracker unavailable, cannot send purchase to dataLayer');
                return;
            }
            this.purchaseTracker.trackPurchase(purchaseData);
        },
        /**
         * Track one-click purchase (fast order)
         * @param {Object} event - Click event
         */
        trackOneClickPurchase(event) {
            if (!this.currentProduct) {
                console.warn('⚠️ No current product for one-click purchase tracking');
                return;
            }
            const productData = this.formatProductForTracking(this.currentProduct, this.amount || 1);
            const transactionId = this.generateTransactionId('fast_order_');
            const purchaseData = {
                transaction_id: transactionId,
                value: productData.price * productData.quantity, // Calculate total value
                currency: 'UAH',
                phone_number: '', // Will be filled when order is confirmed
                email: '', // Will be filled when order is confirmed
                items: [productData]
            };
            // Store for later use when order is confirmed
            localStorage.setItem('pendingFastOrder', JSON.stringify(purchaseData));
        },
        /**
         * Track one-click purchase with user data (called after form submission)
         * @param {Object} product - Product object
         * @param {number} quantity - Product quantity
         * @param {Object} userInfo - User information from form
         */
        trackOneClickPurchaseWithUserData(product, quantity, userInfo) {
            const productData = this.formatProductForTracking(product, quantity);
            const transactionId = this.generateTransactionId('fast_order_');
            const purchaseData = {
                transaction_id: transactionId,
                value: productData.price * productData.quantity,
                currency: 'UAH',
                phone_number: userInfo.phone || '',
                email: userInfo.email || '',
                first_name: userInfo.name || userInfo.first_name || '',
                last_name: userInfo.secondName || userInfo.last_name || '',
                items: [productData]
            };
            // Store for later use when order is confirmed
            localStorage.setItem('pendingFastOrder', JSON.stringify(purchaseData));
        },
        /**
         * Track one-click purchase with provided data
         * @param {Object} productData - Product data
         */
        trackOneClickPurchaseWithData(productData) {
            const transactionId = this.generateTransactionId('fast_order_');

            const purchaseData = {
                transaction_id: transactionId,
                value: productData.totalPrice,
                currency: 'UAH',
                phone_number: '', // Will be filled when order is confirmed
                email: '', // Will be filled when order is confirmed
                items: [productData]
            };
            // Store for later use when order is confirmed
            localStorage.setItem('pendingFastOrder', JSON.stringify(purchaseData));
        },
        /**
         * Format product data for tracking
         * @param {Object} product - Product object
         * @param {number} quantity - Product quantity
         * @returns {Object} Formatted product data
         */
        formatProductForTracking(product, quantity = 1) {
            // Извлекаем цену используя логику из checkoutTracker
            const price = this.extractPrice(product);
            
            // Визначаємо бренд з пріоритетом:
            // 1. З даних товару (productBrand.title або brand.title)
            // 2. З заголовка товару (евристика) - ОБОВ'ЯЗКОВО
            // 3. Фолбек
            let finalBrand = product.productBrand?.title || product.brand?.title || '';
            // ЗАВЖДИ намагаємося витягнути бренд з заголовка, навіть якщо є productBrand
            if (product.title) {
                const extractedBrand = this.extractBrandFromTitle(product.title);
                // Якщо витягли бренд з заголовка, використовуємо його
                if (extractedBrand && extractedBrand.trim()) {
                    finalBrand = extractedBrand;
                }
            }
            if (!finalBrand || !finalBrand.trim()) {
                finalBrand = 'Невизначений бренд';
            }
            // Визначаємо категорію (як в productViewTracker.js)
            let finalCategory = '';
            // Спочатку спробуємо отримати з роуту (каталогу)
            const routeCategory = this.getCurrentCategoryFromRoute();
            if (routeCategory) {
                const categoryName = this.getCategoryNameFromCatalog(routeCategory);
                if (categoryName) {
                    finalCategory = categoryName;
                }
            }
            // Якщо не знайшли в роуті, спробуємо sessionStorage
            if (!finalCategory) {
                try {
                    if (typeof window !== 'undefined' && window.sessionStorage) {
                        finalCategory = sessionStorage.getItem('ga_last_item_category') || '';
                    }
                } catch (e) { /* no-op */ }
            }
            
            // Якщо все ще не знайшли, спробуємо з даних товару
            if (!finalCategory && product.category) {
                finalCategory = product.category.name || product.category.title || '';
            }
            
            if (!finalCategory) {
                finalCategory = 'Невизначена категорія';
            }

            // Витягуємо product_price_id використовуючи ту саму логіку що й basketQuantityTracker
            const productPriceId = this.extractProductId(product);
            const categoryHierarchy = this.extractCategoryHierarchyFromProduct(product);

            return {
                item_name: this.extractProductTitle(product),
                item_id: this.normalizeId(productPriceId),
                price: price,
                currency: 'UAH',
                item_brand: finalBrand,
                item_category: categoryHierarchy.category || finalCategory,
                item_category2: categoryHierarchy.category2,
                item_category3: categoryHierarchy.category3,
                item_category4: categoryHierarchy.category4,
                item_category5: categoryHierarchy.category5,
                quantity: parseInt(quantity)
            };
        },

        /**
         * Format one-click order data for purchase tracking
         * @param {Object} product - Product object
         * @param {number} quantity - Product quantity
         * @param {Object} userInfo - User information
         * @returns {Object} Formatted purchase data
         */
        formatOneClickDataForPurchase(product, quantity, userInfo) {
            // Извлекаем цену используя логику из checkoutTracker
            const price = this.extractPrice(product);
            const totalPrice = price * quantity;
            const transactionId = this.generateTransactionId('fast_order_');
            const categoryHierarchy = this.extractCategoryHierarchyFromProduct(product);
            return {
                transaction_id: transactionId,
                value: totalPrice,
                currency: 'UAH',
                phone_number: userInfo.phone || '',
                email: userInfo.email || '',
                items: [{
                    item_name: this.extractProductTitle(product),
                    item_id: this.normalizeId(this.extractProductId(product)),
                    price: price,
                    currency: 'UAH',
                    item_brand: this.extractBrandFromProduct(product),
                    item_category: categoryHierarchy.category || this.extractCategoryFromProduct(product),
                    item_category2: categoryHierarchy.category2,
                    item_category3: categoryHierarchy.category3,
                    item_category4: categoryHierarchy.category4,
                    item_category5: categoryHierarchy.category5,
                    quantity: parseInt(quantity)
                }]
            };
        },
        /**
         * Витягує правильний ID товару з пріоритетом product_price_id
         * Використовує ту саму логіку що й basketQuantityTracker.js
         * @param {Object} productData - Дані товару
         * @returns {any} ID товару
         */
        extractProductId(productData) {
            if (!productData) {
                console.warn('🔍 [PURCHASE MIXIN] extractProductId: productData is null/undefined');
                return '';
            }
            
            // Пріоритет 1: product_price_id на верхньому рівні об'єкта (НАЙВАЖЛИВІШЕ - справжній ID товару з кошика)
            const topLevelProductPriceId = productData.product_price_id;
            if (topLevelProductPriceId !== null && topLevelProductPriceId !== undefined && topLevelProductPriceId !== '') {
                console.log('✅ [PURCHASE MIXIN] Found product_price_id on top level (НАЙВАЖЛИВІШЕ):', topLevelProductPriceId);
                return topLevelProductPriceId;
            }
            
            // Пріоритет 2: productPriceId (camelCase) на верхньому рівні об'єкта
            const topLevelProductPriceIdCamel = productData.productPriceId;
            if (topLevelProductPriceIdCamel !== null && topLevelProductPriceIdCamel !== undefined && topLevelProductPriceIdCamel !== '') {
                console.log('✅ [PURCHASE MIXIN] Found productPriceId on top level (camelCase):', topLevelProductPriceIdCamel);
                return topLevelProductPriceIdCamel;
            }
            
            // Пріоритет 3: productPrice.price.productPriceId (camelCase - основне місце з API)
            const priceProductPriceId = productData.productPrice?.price?.productPriceId;
            if (priceProductPriceId !== null && priceProductPriceId !== undefined && priceProductPriceId !== '') {
                console.log('✅ [PURCHASE MIXIN] Found productPriceId in productPrice.price.productPriceId:', priceProductPriceId);
                return priceProductPriceId;
            }
            
            // Пріоритет 4: productPrice.priceOld.productPriceId (camelCase - якщо є знижка)
            const priceOldProductPriceId = productData.productPrice?.priceOld?.productPriceId;
            if (priceOldProductPriceId !== null && priceOldProductPriceId !== undefined && priceOldProductPriceId !== '') {
                console.log('✅ [PURCHASE MIXIN] Found productPriceId in productPrice.priceOld.productPriceId:', priceOldProductPriceId);
                return priceOldProductPriceId;
            }
            
            // Пріоритет 5: productPrice.price.product_price_id (snake_case fallback)
            const snakeCasePriceId = productData.productPrice?.price?.product_price_id;
            if (snakeCasePriceId !== null && snakeCasePriceId !== undefined && snakeCasePriceId !== '') {
                console.log('✅ [PURCHASE MIXIN] Found product_price_id in productPrice.price.product_price_id:', snakeCasePriceId);
                return snakeCasePriceId;
            }
            
            // Пріоритет 6: productPrice.price_old.product_price_id (snake_case fallback)
            const snakeCasePriceOldId = productData.productPrice?.price_old?.product_price_id;
            if (snakeCasePriceOldId !== null && snakeCasePriceOldId !== undefined && snakeCasePriceOldId !== '') {
                console.log('✅ [PURCHASE MIXIN] Found product_price_id in productPrice.price_old.product_price_id:', snakeCasePriceOldId);
                return snakeCasePriceOldId;
            }
            
            // Пріоритет 6a: select_type.productPriceId (camelCase, для товарів з кошика)
            const selectTypeProductPriceIdCamel = productData.select_type?.productPriceId;
            if (selectTypeProductPriceIdCamel !== null && selectTypeProductPriceIdCamel !== undefined && selectTypeProductPriceIdCamel !== '') {
                return selectTypeProductPriceIdCamel;
            }
            
            // Пріоритет 6b: select_type.product_price_id (snake_case, для товарів з кошика)
            const selectTypeProductPriceId = productData.select_type?.product_price_id;
            if (selectTypeProductPriceId !== null && selectTypeProductPriceId !== undefined && selectTypeProductPriceId !== '') {
                return selectTypeProductPriceId;
            }
            
            // Пріоритет 7: product.product_price_id (для товарів з кошика)
            const productProductPriceId = productData.product?.product_price_id;
            if (productProductPriceId !== null && productProductPriceId !== undefined && productProductPriceId !== '') {
                return productProductPriceId;
            }
            
            // Пріоритет 8: select_type.id (для товарів з кошика - це може бути product_price_id)
            const selectTypeId = productData.select_type?.id;
            if (selectTypeId !== null && selectTypeId !== undefined && selectTypeId !== '') {
                console.warn('⚠️ [PURCHASE MIXIN] Using select_type.id as productPriceId (might be wrong!):', selectTypeId);
                return selectTypeId;
            }
            
            // Пріоритет 9: id на верхньому рівні (fallback)
            const topLevelId = productData.id;
            if (topLevelId !== null && topLevelId !== undefined && topLevelId !== '') {
                console.warn('⚠️ [PURCHASE MIXIN] Using top-level id as productPriceId (might be wrong!):', topLevelId);
                return topLevelId;
            }
            
            // Якщо product_price_id не знайдено - повертаємо порожній рядок
            console.error('❌ [PURCHASE MIXIN] productPriceId NOT FOUND in any location!');
            return '';
        },
        /**
         * Нормалізує ID до числа, якщо можливо, або повертає рядок/порожнє значення
         * Використовує ту саму логіку що й basketQuantityTracker.js
         * @param {any} id - ID для нормалізації
         * @returns {number|string} Нормалізований ID
         */
        normalizeId(id) {
            if (id === null || id === undefined || id === '') {
                console.warn('⚠️ [PURCHASE MIXIN] normalizeId: Empty or null ID provided:', id);
                return '';
            }
            const num = Number(id);
            if (!Number.isNaN(num) && Number.isFinite(num) && num > 0) {
                return num;
            }
            // Якщо не число або 0, повертаємо рядок
            const str = String(id);
            console.warn('⚠️ [PURCHASE MIXIN] normalizeId: ID is not a valid number, returning string:', str);
            return str;
        },
        /**
         * Generate unique transaction ID
         * @param {string} prefix - Optional prefix
         * @returns {string} Unique transaction ID
         */
        generateTransactionId(prefix = '') {
            const timestamp = Date.now();
            const random = Math.random().toString(36).substr(2, 9);
            return `${prefix}${timestamp}_${random}`;
        },
        /**
         * Track successful payment
         * @param {Object} orderData - Order data
         */
        trackSuccessfulPayment(orderData) {
            if (!orderData || !orderData.transaction_id) {
                console.warn('Invalid order data for payment tracking');
                return;
            }
            // Check if this transaction was already processed
            if (this.purchaseTracker && this.purchaseTracker.isTransactionProcessed(orderData.transaction_id)) {
                console.warn('Transaction already processed:', orderData.transaction_id);
                return;
            }
            this.trackPurchase(orderData);
        },
        /**
         * Track regular add to cart (for comparison)
         * @param {Object} product - Product object
         * @param {number} quantity - Product quantity
         */
        trackAddToCart(product, quantity = 1) {
            // This is just for logging, actual add to cart tracking would be separate
            // and not a purchase event
        },
        /**
         * Format order data for purchase tracking (for regular orders)
         * @param {Object} orderResult - Order result from API
         * @param {Array} basketProducts - Products in basket
         * @param {Object} userInfo - User information
         * @returns {Object} Formatted purchase data
         */
        formatOrderDataForPurchase(orderResult, basketProducts, userInfo) {
            const orderId = orderResult?.orderId || orderResult?.order_id || null;
            const transactionId = orderId
                ? `order_${orderId}`
                : this.generateTransactionId('order_');
            // Calculate total value
            let totalValue = 0;
            const items = basketProducts.map((item, index) => {
                console.log(`\n📦 [PURCHASE MIXIN] Обробка товару #${index + 1} для Purchase:`);
                console.log('   - Повні дані товару:', item);
                
                // Используем основную логику как в быстрой покупке - берем основной товар
                let product = item.product || item;
                console.log('   - Основні дані товару (product):', {
                    id: product.id,
                    title: product.title,
                    productPrice: product.productPrice,
                    select_type: item.select_type,
                    price: item.price,
                    total: item.total
                });
                
                // ПРИОРИТЕТ 1: Используем готовую цену из item.price (если есть)
                // ПРИОРИТЕТ 2: Используем item.total / quantity (если есть total)
                // ПРИОРИТЕТ 3: Используем extractPrice из select_type или product
                let pricePerUnit = 0;
                const quantity = parseInt(item.quantity || 1);
                
                if (item.price && typeof item.price === 'number' && item.price > 0) {
                    // Якщо є готова ціна в item.price - використовуємо її
                    pricePerUnit = item.price;
                    console.log('   - Використовуємо item.price:', pricePerUnit);
                } else if (item.total && typeof item.total === 'number' && item.total > 0 && quantity > 0) {
                    // Якщо є загальна вартість - ділимо на кількість
                    pricePerUnit = item.total / quantity;
                    console.log('   - Використовуємо item.total / quantity:', item.total, '/', quantity, '=', pricePerUnit);
                } else if (item.select_type && item.select_type.price) {
                    // Якщо є select_type з ціною
                    pricePerUnit = parseFloat(item.select_type.price) || this.extractPrice(item.select_type);
                    console.log('   - Використовуємо select_type.price:', pricePerUnit);
                } else {
                    // Fallback - витягуємо ціну через extractPrice
                    console.log('   - Використовуємо extractPrice з product');
                    pricePerUnit = this.extractPrice(product);
                }
                console.log('   - Фінальна ціна за одиницю:', pricePerUnit);
                
                // Обчислюємо загальну вартість товару
                // ПРИОРИТЕТ: використовуємо item.total, якщо воно є, інакше обчислюємо
                let itemTotal = 0;
                if (item.total && typeof item.total === 'number' && item.total > 0) {
                    // Якщо є готова загальна вартість - використовуємо її
                    itemTotal = item.total;
                    console.log('   - Використовуємо item.total для загальної вартості:', itemTotal);
                } else {
                    // Обчислюємо загальну вартість
                    itemTotal = pricePerUnit * quantity;
                    console.log('   - Обчислюємо загальну вартість:', pricePerUnit, '*', quantity, '=', itemTotal);
                }
                totalValue += itemTotal;
                
                // Витягуємо product_price_id використовуючи ту саму логіку що й basketQuantityTracker
                console.log('\n   🔍 [PURCHASE MIXIN] Пошук product_price_id для товару:');
                console.log('      - item.id:', item.id);
                console.log('      - item.product_price_id:', item.product_price_id);
                console.log('      - item.productPriceId:', item.productPriceId);
                console.log('      - item.select_type?.product_price_id:', item.select_type?.product_price_id);
                console.log('      - item.select_type?.productPriceId:', item.select_type?.productPriceId);
                console.log('      - item.product?.product_price_id:', item.product?.product_price_id);
                console.log('      - item.productPrice?.price?.productPriceId:', item.productPrice?.price?.productPriceId);
                console.log('      - item.productPrice?.price?.product_price_id:', item.productPrice?.price?.product_price_id);
                
                const productPriceId = this.extractProductId(item);
                console.log('   ✅ [PURCHASE MIXIN] Витягнутий product_price_id:', productPriceId, '(тип:', typeof productPriceId, ')');
                
                // ПЕРЕВІРКА: переконуємося, що використовуємо product_price_id, а не product.id
                if (productPriceId && productPriceId !== item.id) {
                    console.log('   ✅ [PURCHASE MIXIN] ПЕРЕВІРКА: product_price_id (' + productPriceId + ') ≠ product.id (' + item.id + ') - ВСЕ ПРАВИЛЬНО!');
                } else if (productPriceId === item.id) {
                    console.warn('   ⚠️ [PURCHASE MIXIN] УВАГА: product_price_id співпадає з product.id - можливо, використовується неправильний ID!');
                }
                
                const normalizedId = this.normalizeId(productPriceId);
                console.log('   ✅ [PURCHASE MIXIN] Нормалізований ID (item_id для GA):', normalizedId, '(тип:', typeof normalizedId, ')');
                console.log('   🎯 [PURCHASE MIXIN] ЦЕ БУДЕ item_id В GOOGLE ANALYTICS:', normalizedId);
                
                const brand = this.extractBrandFromProduct(product);
                const category = this.extractCategoryFromProduct(product);
                const categoryHierarchy = this.extractCategoryHierarchyFromProduct(product);
                const itemName = this.extractProductTitle(product, item);
                
                const formattedItem = {
                    item_name: itemName,
                    item_id: normalizedId, // product_price_id
                    price: pricePerUnit,
                    currency: 'UAH',
                    item_brand: brand,
                    item_category: categoryHierarchy.category || category,
                    item_category2: categoryHierarchy.category2,
                    item_category3: categoryHierarchy.category3,
                    item_category4: categoryHierarchy.category4,
                    item_category5: categoryHierarchy.category5,
                    quantity: quantity
                };
                
                console.log('   📦 [PURCHASE MIXIN] Сформований товар для Purchase:');
                console.log('      - item_id:', formattedItem.item_id, '(це product_price_id, НЕ product.id!)');
                console.log('      - item_name:', formattedItem.item_name);
                console.log('      - price:', formattedItem.price, formattedItem.currency);
                console.log('      - quantity:', formattedItem.quantity);
                console.log('      - item_brand:', formattedItem.item_brand);
                console.log('      - item_category:', formattedItem.item_category);
                console.log('   💰 [PURCHASE MIXIN] Загальна вартість товару:', itemTotal);
                
                return formattedItem;
            });
            return {
                transaction_id: transactionId,
                orderId: orderId,
                value: totalValue,
                currency: 'UAH',
                phone_number: userInfo?.phone || '',
                email: userInfo?.email || '',
                items: items
            };
        },
        /**
         * Category hierarchy for GA item_category / item_category2..5
         */
        extractCategoryHierarchyFromProduct(product) {
            const cat = product?.category || product?.select_type?.category || null;
            if (!cat) {
                return {
                    category: '',
                    category2: '',
                    category3: '',
                    category4: '',
                    category5: '',
                };
            }

            return {
                category: cat.name || cat.title || '',
                category2: cat.parent?.name || cat.parent?.title || '',
                category3: cat.parent?.parent?.name || cat.parent?.parent?.title || '',
                category4: cat.parent?.parent?.parent?.name || cat.parent?.parent?.parent?.title || '',
                category5: cat.parent?.parent?.parent?.parent?.name || cat.parent?.parent?.parent?.parent?.title || '',
            };
        },
        /**
         * Handle payment success callback from URL parameters.
         * Purchase is sent only after backend confirms payment is complete.
         * Payload comes from API when localStorage is empty.
         */
        async handlePaymentSuccessCallback() {
            const urlParams = new URLSearchParams(window.location.search);
            let orderId = urlParams.get('order_id') || urlParams.get('order-id');

            const storedOrderData = localStorage.getItem('lastOrderData');
            if (!orderId && storedOrderData) {
                try {
                    orderId = JSON.parse(storedOrderData)?.orderId || null;
                } catch (error) {
                    // ignore
                }
            }

            console.log('🔍 [PURCHASE MIXIN] handlePaymentSuccessCallback', {orderId, hasLocal: !!storedOrderData});

            if (!orderId) {
                console.warn('🔍 [PURCHASE MIXIN] No order id for payment success callback, skip');
                return;
            }

            const {trackPurchaseForOrderId} = await import('@/utils/purchasePayload');
            await trackPurchaseForOrderId(orderId);
        },
        /**
         * Check for payment success in URL parameters and track purchase
         * This should be called on page load for payment success pages
         */
        checkPaymentSuccessAndTrack() {
            const urlParams = new URLSearchParams(window.location.search);
            const paymentStatus = urlParams.get('status');

            // Explicit provider success only. Monobank/LiqPay returns with
            // payment-popup=show are handled by home (status sync + complete guard).
            if (paymentStatus === 'success' || paymentStatus === 'completed') {
                this.handlePaymentSuccessCallback();
            }
        },
        /**
         * Витягує ціну з даних товару (логика из checkoutTracker)
         * @param {Object} productData - Дані товару
         * @param {Element} element - DOM элемент (опционально)
         * @returns {number}
         */
        extractPrice(productData, element = null) {
            // ПРИОРИТЕТ 1: Ищем цену за единицу в данных товара (из store)
            if (productData.productPrice && productData.productPrice.price && productData.productPrice.price.price) {
                const price = parseFloat(productData.productPrice.price.price);
                if (!isNaN(price) && price > 0) {
                    return price;
                }
            }
            
            // ПРИОРИТЕТ 1.1: Проверяем сохраненную цену за м² (для товаров с расчетом по площади)
            try {
                if (typeof window !== 'undefined' && window.sessionStorage) {
                    const savedM2Price = sessionStorage.getItem('ga_m2_price');
                    if (savedM2Price) {
                        const price = parseFloat(savedM2Price);
                        if (!isNaN(price) && price > 0) {
                            return price;
                        }
                    }
                }
            } catch (e) {
                // Игнорируем ошибки sessionStorage
            }
            
            // ПРИОРИТЕТ 2: Ищем цену в других местах структуры данных
            if (productData.price) {
                const price = parseFloat(productData.price);
                if (!isNaN(price) && price > 0) {
                    return price;
                }
            }
            
            if (productData.select_type && productData.select_type.price) {
                const price = parseFloat(productData.select_type.price);
                if (!isNaN(price) && price > 0) {
                    return price;
                }
            }
            
            // ПРИОРИТЕТ 3: Если передан конкретный элемент, ищем цену в нем
            if (element) {
                const priceSelectors = [
                    '.product__price h5', // ПРИОРИТЕТ 1: цена за единицу товара в h5
                    '.product__price', // ПРИОРИТЕТ 2: цена за единицу товара (fallback)
                    '.product-price', // ПРИОРИТЕТ 3: цена товара
                    '.price', // ПРИОРИТЕТ 4: общий селектор цены
                    '[data-price]', // ПРИОРИТЕТ 5: data-атрибут
                    '.cost, .amount' // ПРИОРИТЕТ 6: альтернативные селекторы
                ];
                
                for (const selector of priceSelectors) {
                    const priceElement = element.querySelector(selector);
                    if (priceElement) {
                        // Сначала проверяем data-атрибуты
                        const dataPrice = priceElement.getAttribute('data-price');
                        if (dataPrice) {
                            const parsedPrice = parseFloat(dataPrice);
                            if (!isNaN(parsedPrice) && parsedPrice > 0) {
                                return parsedPrice;
                            }
                        }
                        // Если data-атрибут не найден, парсим текст
                        const priceText = priceElement.textContent?.trim() || '';
                        if (priceText) {
                            // Парсим цену (убираем пробелы и заменяем запятую на точку)
                            const cleanPrice = priceText.replace(/\s/g, '').replace(',', '.');
                            const parsedPrice = parseFloat(cleanPrice);
                            if (!isNaN(parsedPrice) && parsedPrice > 0) {
                                return parsedPrice;
                            }
                        }
                    }
                }
            }
            
            // Fallback: возвращаем 0 если цену не нашли
            return 0;
        },
        /**
         * Extract brand from product object
         * @param {Object} product - Product object
         * @returns {string} Brand name
         */
        extractBrandFromProduct(product) {
            // Визначаємо бренд з пріоритетом:
            // 1. З даних товару (productBrand.title або brand.title)
            // 2. З заголовка товару (евристика) - ОБОВ'ЯЗКОВО
            // 3. Фолбек
            let finalBrand = product.productBrand?.title || product.brand?.title || '';
            // ЗАВЖДИ намагаємося витягнути бренд з заголовка, навіть якщо є productBrand
            if (product.title) {
                const extractedBrand = this.extractBrandFromTitle(product.title);
                // Якщо витягли бренд з заголовка, використовуємо його
                if (extractedBrand && extractedBrand.trim()) {
                    finalBrand = extractedBrand;
                }
            }
            if (!finalBrand || !finalBrand.trim()) {
                finalBrand = 'Невизначений бренд';
            }
            return finalBrand;
        },
        /**
         * Витягує бренд з заголовка товару
         * @param {string} title - Заголовок товару
         * @returns {string}
         */
        extractBrandFromTitle(title) {
            if (!title) {
                return '';
            }
            const parseBrandFromTitle = (title) => {
                const t = (title || '').trim();
                if (!t) return '';
                // Пріоритет 1: Шукаємо відомі бренди з модифікаторами (BauGlas FSX, RODECA, BAUGLAS)
                const knownBrands = ['BauGlas', 'RODECA', 'BAUGLAS'];
                for (const brand of knownBrands) {
                    // Спочатку шукаємо бренд з можливими модифікаторами (FSX, Professional, тощо)
                    const brandWithModifier = t.match(new RegExp(`\\b${brand}\\s+([A-Z]{2,}(?:\\s+[A-Za-z]+)*)\\b`, 'i'));
                    if (brandWithModifier && brandWithModifier[0]) {
                        return brandWithModifier[0];
                    }
                    // Якщо не знайшли з модифікатором, шукаємо просто бренд
                    const brandMatch = t.match(new RegExp(`\\b${brand}\\b`, 'i'));
                    if (brandMatch && brandMatch[0]) {
                        return brandMatch[0];
                    }
                }
                // Додатковий пошук: шукаємо "BauGlas FSX" як окремий випадок
                const bauGlasFSXMatch = t.match(/\bBauGlas\s+FSX\b/i);
                if (bauGlasFSXMatch && bauGlasFSXMatch[0]) {
                    return bauGlasFSXMatch[0];
                }
                // Пріоритет 2: Шукаємо CamelCase (BauGlas, BauGlas FSX)
                const camelMatch = t.match(/\b[A-Z][a-z]+[A-Z][a-zA-Z]+(?:\s+[A-Z]+)*\b/);
                if (camelMatch && camelMatch[0]) {
                    return camelMatch[0];
                }
                // Пріоритет 3: Шукаємо слова з великих літер (RODECA, BAUGLAS, FSX)
                const capsMatch = t.match(/\b[A-ZА-ЯЇІЄҐ]{3,}\b/u);
                if (capsMatch && capsMatch[0]) {
                    return capsMatch[0];
                }
                // Пріоритет 4: Спробуємо знайти бренд до коми
                const commaIdx = t.indexOf(',');
                if (commaIdx > 0) {
                    const beforeComma = t.slice(0, commaIdx).trim();
                    if (beforeComma.length >= 2 && beforeComma.length <= 60) {
                        return beforeComma;
                    }
                }
                // Пріоритет 5: Беремо перше слово з 3+ літер
                const firstWord = t.match(/\b[\p{L}]{3,}\b/u);
                if (firstWord && firstWord[0]) {
                    return firstWord[0];
                }
                return '';
            };
            return parseBrandFromTitle(title);
        },
        /**
         * Extract category from product object
         * @param {Object} product - Product object
         * @returns {string} Category name
         */
        extractCategoryFromProduct(product) {
            // For purchase events, prefer the category from product payload first.
            // Route/session category is only a fallback because it can contain stale context.
            let finalCategory = '';

            // 1) Product payload category
            if (product?.category) {
                finalCategory = product.category.name || product.category.title || '';
            }
            if (!finalCategory && product?.select_type?.category) {
                finalCategory = product.select_type.category.name || product.select_type.category.title || '';
            }

            // 2) Current route category
            if (!finalCategory) {
                const routeCategory = this.getCurrentCategoryFromRoute();
                if (routeCategory) {
                    const categoryName = this.getCategoryNameFromCatalog(routeCategory);
                    if (categoryName) {
                        finalCategory = categoryName;
                    }
                }
            }

            // 3) Session fallback
            if (!finalCategory) {
                try {
                    if (typeof window !== 'undefined' && window.sessionStorage) {
                        finalCategory = sessionStorage.getItem('ga_last_item_category') || '';
                    }
                } catch (e) { /* no-op */ }
            }

            if (!finalCategory) {
                finalCategory = 'Невизначена категорія';
            }

            return finalCategory;
        },
        /**
         * Отримує поточну категорію з роуту
         * @returns {string}
         */
        getCurrentCategoryFromRoute() {
            try {
                // Перевіряємо, чи є Vue router доступний
                if (typeof window !== 'undefined' && window.$nuxt) {
                    const route = window.$nuxt.$route;
                    if (route && route.params && route.params.slug) {
                        return route.params.slug;
                    }
                }
                // Альтернативний спосіб - через URL
                const path = window.location.pathname;
                const pathParts = path.split('/');
                const katalogIndex = pathParts.findIndex(part => part === 'katalog');
                if (katalogIndex !== -1 && pathParts[katalogIndex + 1]) {
                    const slug = pathParts[katalogIndex + 1];
                    return slug;
                }
                return '';
            } catch (e) {
                console.warn('Помилка отримання категорії з роуту:', e);
                return '';
            }
        },
        extractProductTitle(product, sourceItem = null) {
            const candidates = [
                product?.title,
                product?.name,
                product?.label,
                product?.select_type?.title,
                product?.select_type?.name,
                sourceItem?.title,
                sourceItem?.name,
                sourceItem?.label,
                sourceItem?.select_type?.title,
                sourceItem?.select_type?.name,
                sourceItem?.product?.title,
                sourceItem?.product?.name,
                sourceItem?.article ? `Товар ${sourceItem.article}` : '',
                product?.article ? `Товар ${product.article}` : '',
            ];

            const normalized = candidates
                .map((value) => (value || '').toString().trim())
                .find((value) => value.length > 0);

            return normalized || 'Товар без назви';
        },
        /**
         * Отримує назву категорії з каталогу за slug
         * @param {string} slug - Slug категорії
         * @returns {string}
         */
        getCategoryNameFromCatalog(slug) {
            if (!slug) {
                return '';
            }
            // Список каталогів з футера (правильні slug з бази даних)
            const catalogMap = {
                'monolitnii-polikarbonat': 'Монолітний полікарбонат',
                'profilyovanii-polikarbonat': 'Профільований полікарбонат',
                'sotovii-polikarbonat': 'Сотовий полікарбонат',
                'vstavka-do-pokrivli-iz-sendvich-paneli': 'Вставка до покрівлі із сендвіч панелі',
                'svitloprozori-fasadni-systemy': 'Світлопрозорі фасадні системи',
                'svitloprozori-pokrivelni-systemy': 'Світлопрозорі покрівельні системи',
                'aksessuary': 'Аксесуари',
                'rishennya-dlya-avtomobiliv': 'Рішення для автомобілів',
                'agroteplitsi': 'Агротеплиці'
            };
            const categoryName = catalogMap[slug];
            if (categoryName) {
                return categoryName;
            } else {
                return slug;
            }
        },
    }
};
